<template>
    <BaseModal :title="title" @close="handleModal" :visible="true" size="small" customClass="">
        <div class="assetssment__cover__box tab-content tbh__popup__block transcribe__popup__wrap">
            <div class="transcribe__features__group">
                <div class="add__note__test__box response__group__wrapper"
                    :style="{ paddingInline: 0, boxShadow: 'none' }">
                    <div class="cover__group__box" v-for="item in data" :key="item.id">
                        <div class="addnote__box">
                            <label>{{ item.question }}</label>
                            <template v-if="consonantStates(item)?.length">
                                <div class="tbh__labels__group">
                                    <template v-for="(c, index) in consonantStates(item)" :key="index">
                                        <template v-if="c.value === '-'"> - </template>
                                        <template v-else-if="c.value === ' '"> &nbsp; </template>
                                        <template v-else>
                                            <span class="label"
                                                :class="[getClassForState(c.state), { 'disabled-label': true }]"
                                                :title="noResponse ? 'Disabled when No Response is checked' : ''">
                                                {{ c.value }}
                                            </span>
                                        </template>
                                    </template>
                                </div>
                            </template>
                            <!-- How each incorrect sound went wrong, so the
                                 detail says how a sound failed and not only
                                 that it did. "Suggested" means the category
                                 came from the recording, not the clinician. -->
                            <div class="sound__errors" v-if="soundErrors(item).length">
                                <span class="sound__error" v-for="(e, index) in soundErrors(item)" :key="index"
                                    :class="{ suggested: e.source === SOURCE_AI }">
                                    <b>{{ e.value }}</b> {{ e.label }}<template v-if="e.source === SOURCE_AI">
                                        ({{ SOUND_ERROR_SUGGESTED_TAG.toLowerCase() }})</template>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <p class="note-text rlms-fs-xxs rlms-text-secondary-base rlms-mt-2" v-if="hasSoundErrors">
                {{ SOUND_ERROR_LEGEND }}
            </p>
        </div>
        <div class="sepline__divider rlms-mt-5"></div>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-center" style="padding: 0;">
                <RouterLink :to="`/organizations/${organizationId}/learners/${learnerId}/assessments/${assessmentId}`"
                    class="rlms-btn rlms-btn-primary" id="save-org-btn" style="flex: none;">View</RouterLink>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { computed, ref } from 'vue';
import BaseModal from '../BaseModal.vue';
import {
    SOUND_ERROR_LEGEND,
    SOUND_ERROR_SUGGESTED_TAG,
    SOURCE_AI,
    soundErrorEntries,
} from '@/utils/sound-error-categories';

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);
const props = defineProps({
    organizationId: Number,
    learnerId: Number,
    assessmentId: Number,
    data: Object,
});

const title = `Details`;

const showModal = ref(true);

const handleModal = (action = 'close') => {
    showModal.value = false;
    emit(action);
};

const consonantStates = (newItem) => {
    // Parse consonants
    let parsedConsonants = [];
    try {
        if (newItem?.consonant) {
            parsedConsonants = JSON.parse(newItem?.consonant?.replace(/'/g, '"')) || [];
        } else {
            parsedConsonants = newItem?.question.split('-');
        }
    } catch {
        parsedConsonants = [];
    }

    let parsedConsonantScore = [];
    parsedConsonantScore = typeof newItem?.consonant_score === 'string'
        ? JSON.parse(newItem.consonant_score)
        : newItem?.consonant_score || [];


    parsedConsonants = parsedConsonants.map((value, index) => {
        // Unscored slots stay neutral; only a recorded 0 shows as red.
        const score = parsedConsonantScore?.[index];
        let state = 'default';
        if (score == 1 || score === true) state = 'green';
        else if (score == 0 || score === false) state = 'red';
        return { id: index, value, state };
    });

    return parsedConsonants;
}

const getClassForState = (state) => {
    if (state === 'green') return 'green';
    if (state === 'red') return 'red';
    return 'purple';
};

// The categories for one item, lined up with its sounds. The clinician's own
// choice wins sound by sound and the model's suggestion fills the rest, which
// the shared helper works out from `error_categories` when the API sends it and
// from the two raw columns when it does not.
const soundErrors = (item) => {
    const entries = soundErrorEntries(item);
    if (!entries.length) return [];

    return consonantStates(item)
        .map((c, index) => ({ value: c.value, ...(entries[index] || {}) }))
        .filter(e => e.label && e.value !== '-' && e.value !== ' ');
};

const hasSoundErrors = computed(
    () => Object.values(props.data || {}).some(item => soundErrors(item).length > 0)
);
</script>

<style scoped>
.sound__errors {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 10px;
    margin-top: 6px;
}

/* Specific enough to beat the full-width `.addnote__box span` rule around it. */
.sound__errors .sound__error {
    display: inline-block;
    width: auto;
    font-size: 12px;
    line-height: 16px;
    color: #6B7280;
}

/* The model's own, never confirmed by a clinician: it reads as a suggestion
   here too, so nobody takes it for a label somebody chose. The same purple the
   recorder and the Transcribe modal use for an unconfirmed suggestion. */
.sound__errors .sound__error.suggested {
    color: #725CFF;
    font-style: italic;
}
</style>
